import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@lib/utils'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button visual variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  /** Button size */
  size?: 'sm' | 'md' | 'lg'
  /** Show loading spinner */
  loading?: boolean
  /** Icon to show before text */
  leftIcon?: ReactNode
  /** Icon to show after text */
  rightIcon?: ReactNode
  /** Full width button */
  fullWidth?: boolean
}

/**
 * Button component with brutalist styling
 *
 * @example
 * <Button variant="primary">Click me</Button>
 * <Button variant="secondary" size="lg" leftIcon={<Icon />}>With Icon</Button>
 * <Button loading>Saving...</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      fullWidth = false,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center gap-2 font-semibold transition-all',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          'disabled:cursor-not-allowed',

          // Size variants
          size === 'sm' && 'h-8 px-3 text-body-sm',
          size === 'md' && 'h-10 px-5 text-body',
          size === 'lg' && 'h-12 px-6 text-body-lg',

          // Variant styles
          variant === 'primary' && [
            'bg-primary text-text-inverse',
            'shadow-brutalist',
            'hover:bg-primary-hover hover:shadow-brutalist-hover',
            'active:bg-primary-pressed active:shadow-brutalist-pressed active:translate-x-[2px] active:translate-y-[2px]',
            'disabled:bg-surface-overlay disabled:text-text-muted disabled:shadow-none',
          ],

          variant === 'secondary' && [
            'bg-transparent text-primary border-2 border-primary',
            'shadow-brutalist',
            'hover:bg-primary-muted hover:shadow-brutalist-hover',
            'active:bg-primary-muted active:shadow-brutalist-pressed active:translate-x-[2px] active:translate-y-[2px]',
            'disabled:border-border disabled:text-text-muted disabled:shadow-none',
          ],

          variant === 'ghost' && [
            'bg-transparent text-text-primary',
            'hover:bg-surface-elevated hover:text-primary',
            'active:bg-surface-overlay',
            'disabled:text-text-muted disabled:bg-transparent',
          ],

          variant === 'danger' && [
            'bg-error text-white',
            'shadow-brutalist-error',
            'hover:bg-error/90 hover:shadow-[6px_6px_0_0_#FF4444]',
            'active:shadow-[2px_2px_0_0_#FF4444] active:translate-x-[2px] active:translate-y-[2px]',
            'disabled:bg-surface-overlay disabled:text-text-muted disabled:shadow-none',
          ],

          // Full width
          fullWidth && 'w-full',

          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <LoadingSpinner size={size} />
            <span>{children}</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="shrink-0">{leftIcon}</span>}
            {children && <span>{children}</span>}
            {rightIcon && <span className="shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

// Loading spinner component
function LoadingSpinner({ size }: { size: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  }

  return (
    <svg
      className={cn('animate-spin', sizeClasses[size])}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

/**
 * Icon-only button variant
 */
export interface IconButtonProps
  extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'> {
  /** Icon to display */
  icon: ReactNode
  /** Accessible label */
  'aria-label': string
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, icon, size = 'md', ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center transition-all',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          'disabled:cursor-not-allowed',

          // Size (square aspect)
          size === 'sm' && 'h-8 w-8',
          size === 'md' && 'h-10 w-10',
          size === 'lg' && 'h-12 w-12',

          // Default ghost style for icon buttons
          'bg-transparent text-text-secondary',
          'hover:bg-surface-elevated hover:text-primary',
          'active:bg-surface-overlay',
          'disabled:text-text-muted disabled:bg-transparent',

          className
        )}
        {...props}
      >
        {icon}
      </button>
    )
  }
)

IconButton.displayName = 'IconButton'

export default Button
